/**
 * S0 epoch machinery (PC-B rev 9, section 10).
 *
 * AUTH EPOCH: an in-document counter advanced SYNCHRONOUSLY before state
 * exposure on sign-in, sign-out, or user change — including A-to-B-to-A —
 * and NOT advanced by a same-owner token refresh. Preview/export/poll/
 * delivery bindings capture it and discard any result once it moves.
 * Bindings never survive reload (the counter is per-document by design).
 *
 * LOCAL PRAYER EPOCH: one DURABLE browser-wide counter (IDB, keepsake-meta
 * store) advanced on every local prayer mutation or keepsake-policy
 * intent and broadcast to all tabs. Broadcast is only an optimization: on
 * focus, pageshow, BFCache restore, and immediately before accepting any
 * preview/export/poll/status/delivery result, the client REREADS the
 * durable IDB epoch (and scans unresolved fences + the validity journal)
 * so a suspended tab that missed every message still fails closed.
 */

// Lazy supabase import: supabase.ts reads import.meta.env at module
// scope, which only exists under Vite — a static import here would make
// this module (and fences.ts, which the probes load under tsx)
// unloadable outside the bundler.
import * as db from '@/lib/db';

// ---------------------------------------------------------------------------
// Auth epoch
// ---------------------------------------------------------------------------

let authEpoch = 0;
let lastUserId: string | null | undefined = undefined; // undefined = unresolved
let authResolved = false;
let authWired = false;
let resolveInitialAuth: (() => void) | null = null;
const initialAuthPromise = new Promise<void>((resolve) => {
  resolveInitialAuth = resolve;
});

function finishInitialAuthResolution(userId: string | null): void {
  if (authResolved) return;
  lastUserId = userId;
  authResolved = true;
  resolveInitialAuth?.();
  resolveInitialAuth = null;
}

/** Wire the auth listener once, on demand; main.tsx does not call this at boot. */
export function initKeepsakeEpochs(): void {
  if (authWired) return;
  authWired = true;
  void wireAuth();
}

async function wireAuth(): Promise<void> {
  let sb: ReturnType<typeof import('@/lib/supabase').getSupabase>;
  try {
    sb = (await import('@/lib/supabase')).getSupabase();
  } catch {
    sb = null;
  }
  if (!sb) {
    // No auth client: resolve as signed-out so reads don't hang.
    finishInitialAuthResolution(null);
    return;
  }
  sb.auth.getSession().then(({ data }) => {
    const uid = data.session?.user?.id ?? null;
    if (!authResolved) {
      finishInitialAuthResolution(uid);
    }
  }).catch(() => {
    if (!authResolved) {
      finishInitialAuthResolution(null);
    }
  });
  sb.auth.onAuthStateChange((_event, session) => {
    const uid = session?.user?.id ?? null;
    if (!authResolved) {
      finishInitialAuthResolution(uid);
      return;
    }
    // Same-owner token refresh does not advance; any user change
    // (including sign-out and A->B->A) advances synchronously.
    if (uid !== lastUserId) {
      lastUserId = uid;
      authEpoch++;
    }
  });
}

export function getAuthEpoch(): number {
  return authEpoch;
}

/** Resolved current user id, or null signed-out, or undefined unresolved. */
export function getAuthUserId(): string | null | undefined {
  return authResolved ? (lastUserId ?? null) : undefined;
}

export function isAuthResolved(): boolean {
  return authResolved;
}

/** Node-probe-only auth transition for deterministic owner isolation tests. */
export function __setAuthUserIdForProbe(userId: string | null): void {
  if (!db.isNodeProbeRuntime()) {
    throw new Error('auth-probe-override-unavailable-in-browser');
  }
  if (!authResolved) {
    finishInitialAuthResolution(userId);
    return;
  }
  if (userId !== lastUserId) {
    lastUserId = userId;
    authEpoch += 1;
  }
}

/** Resolve only after the first session/error result has installed owner+epoch state. */
export async function waitForAuthResolution(): Promise<void> {
  initKeepsakeEpochs();
  if (authResolved) return;
  await initialAuthPromise;
}

// ---------------------------------------------------------------------------
// Local prayer epoch (durable)
// ---------------------------------------------------------------------------

const CHANNEL_NAME = 'adia-keepsake-epoch-v1';
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

/** Reread the DURABLE epoch from IDB (never a cached in-memory value). */
export async function readLocalPrayerEpoch(): Promise<string> {
  return db.getKeepsakeEpoch();
}

/** Broadcast an epoch advance to other tabs (optimization only). */
export function broadcastLocalPrayerEpoch(value: string): void {
  try {
    getChannel()?.postMessage({ kind: 'epoch', value });
  } catch {
    // Broadcast is an optimization; the durable reread is the guarantee.
  }
}

/**
 * Subscribe to cross-tab epoch broadcasts. The callback treats every
 * message as "reread durable state now", never as authority.
 */
export function onLocalPrayerEpochBroadcast(cb: () => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handler = () => cb();
  ch.addEventListener('message', handler);
  return () => ch.removeEventListener('message', handler);
}
